import { FC, useEffect } from "react";
import * as Yup from "yup";
import { useHandleForm } from "../../../../hooks/useHandleForm";
import { useStatusMessage } from "../../../../hooks/useStatusMessage";
import { FormField } from "../../../../ui-kit/FormField/FormField";
import { Controller } from "react-hook-form";
import { ICheckbox, ISelect, Spinner } from "../../../../ui-kit";
import styles from "./OrganizationForm.module.scss";
import { IconButton } from "../../../../ui-kit/IButton/IconButton/IconButton";
import { runInAction } from "mobx";
import nsiOrgStore from "../../../../store/nsiOrganizationsStore";
import { observer } from "mobx-react-lite";
import { pick } from "lodash";
import { FormBlock } from "../../../../ui-kit/FormBlock";

const schema = Yup.object().shape({
  name: Yup.string()
    .nullable(true)
    .transform((value, original) =>
      original === null || original === "" ? "" : value,
    )
    .required("Введите название"),
  isGrbs: Yup.boolean(),
  abbreviation: Yup.string()
    .nullable(true)
    .when("isGrbs", {
      is: true,
      then: Yup.string()
        .nullable(true)
        .transform((value, original) =>
          original === null || original === "" ? "" : value,
        )
        .required("Введите сокращение"),
    })
    .when("isRso", {
      is: true,
      then: Yup.string()
        .nullable(true)
        .transform((value, original) =>
          original === null || original === "" ? "" : value,
        )
        .required("Введите сокращение"),
    }),
  inn: Yup.string()
    .nullable(true)
    .transform((value) =>
      typeof value !== "string" ? "" : value.replace(/\s/g, ""),
    )
    .typeError("Не заполнены обязательные поля")
    .required("Не заполнены обязательные поля"),
  ogrn: Yup.string()
    .nullable(true)
    .transform((value) =>
      typeof value !== "string" ? "" : value.replace(/\s/g, ""),
    )
    .typeError("Не заполнены обязательные поля")
    .required("Не заполнены обязательные поля"),
});

interface IOrgForm {
  id: number;
  name: string;
  inn: string;
  grbsId: number;
  isGrbs: boolean;
  isRso: boolean;
  gridTypeIds: number[];
  gridTypes: any;
  grbsTypeId: number;
}

const OrganizationForm: FC<{
  organization: IOrgForm;
  closeForm?: () => void;
}> = observer(({ organization, closeForm }) => {
  const {
    errors,
    setValue,
    register,
    watchAllField,
    control,
    handleSubmit,
    reset,
  } = useHandleForm(schema, organization);

  const onSubmit = (data) => {
    runInAction(async () => {
      nsiOrgStore
        .updateOrganization(
          pick(data, [
            "id",
            "name",
            "grbsId",
            "grbsTypeId",
            "inn",
            "isGrbs",
            "isRso",
            "gridTypeIds",
            "ogrn",
            "isTko",
            "abbreviation",
            "hasSpPermission",
          ]),
        )
        .then((res) => {
          if (res.successful) {
            reset();
            closeForm();
          }
        });
    });
  };

  useEffect(() => {
    if (watchAllField.isGrbs) {
      setValue("grbsId", organization.id);
      setValue("isRso", false);
      setValue("gridTypeIds", []);
    } else setValue("grbsTypeId", null);
  }, [watchAllField.isGrbs]);

  useEffect(() => {
    if (watchAllField.isRso) {
      if (watchAllField.grbsId === organization.id) {
        setValue("isGrbs", false);
        setValue("grbsId", null);
      }
    } else {
      setValue("gridTypeIds", []);
      setValue("hasSpPermission", false);
    }
  }, [watchAllField.isRso]);

  useStatusMessage(nsiOrgStore.messageStatus);

  return (
    <form onSubmit={handleSubmit(onSubmit)} className={styles.form}>
      <FormBlock className={styles.formBlock}>
        <div className={styles.formGrid}>
          <FormField
            type="text"
            label="Наименование организации"
            name={"name"}
            currentValue={watchAllField.name}
            register={register}
            isRequired
            error={errors.name?.message}
          />

          <Controller
            control={control}
            name="grbsId"
            render={({ field }) => (
              <ISelect
                field={field}
                optionsArr={nsiOrgStore.grbsArray}
                placeholder="ГРБС"
                disabled={watchAllField.isGrbs}
              />
            )}
          />
          <Controller
            control={control}
            name="grbsTypeId"
            render={({ field }) => (
              <ISelect
                field={field}
                optionsArr={nsiOrgStore.typesRGBS}
                placeholder="Тип ГРБС"
                disabled={!watchAllField.isGrbs}
              />
            )}
          />

          <FormField
            type="number"
            label="ИНН"
            name={"inn"}
            currentValue={watchAllField.inn}
            register={register}
            isRequired
            error={errors.inn?.message}
          />

          <FormField
            type="number"
            label="ОГРН"
            name={"ogrn"}
            currentValue={watchAllField.ogrn}
            register={register}
            isRequired
            error={errors.ogrn?.message}
          />
          {(watchAllField.isGrbs || watchAllField.isRso) && (
            <FormField
              type="text"
              label="Сокращение"
              name={"abbreviation"}
              currentValue={watchAllField.abbreviation}
              register={register}
              isRequired
              error={errors?.abbreviation?.message}
            />
          )}

          <div className={styles.checkboxes}>
            {!watchAllField.isRso && (
              <Controller
                control={control}
                name="isGrbs"
                render={({ field: { value, onChange } }) => (
                  <ICheckbox
                    checked={value}
                    onChange={onChange}
                    label={"ГРБС"}
                    className={styles.checkbox}
                  />
                )}
              />
            )}

            {!watchAllField.isGrbs && (
              <Controller
                control={control}
                name="isRso"
                render={({ field: { value, onChange } }) => (
                  <ICheckbox
                    checked={value}
                    onChange={onChange}
                    label={"РСО"}
                    className={styles.checkbox}
                  />
                )}
              />
            )}
            <Controller
              control={control}
              name="isTko"
              render={({ field: { value, onChange } }) => (
                <ICheckbox
                  checked={value}
                  onChange={onChange}
                  label={"ТКО"}
                  className={styles.checkbox}
                />
              )}
            />
            {watchAllField.isRso && (
              <Controller
                control={control}
                name="hasSpPermission"
                render={({ field: { value, onChange } }) => (
                  <ICheckbox
                    checked={value}
                    onChange={onChange}
                    label="для СП"
                    className={styles.checkbox}
                  />
                )}
              />
            )}
          </div>

          <div>
            {watchAllField.isRso && (
              <Controller
                control={control}
                name="gridTypeIds"
                render={({ field }) => {
                  return (
                    <ISelect
                      mode="multiple"
                      field={field}
                      optionsArr={nsiOrgStore.gridTypesList}
                      placeholder={"Тип ресурса"}
                    />
                  );
                }}
              />
            )}
          </div>

          <div className={styles.buttons}>
            <IconButton
              typeIcon="new"
              disabled={nsiOrgStore.formsSubmitLoading[organization.id]}
            >
              {nsiOrgStore.formsSubmitLoading[organization.id] ? (
                <Spinner />
              ) : (
                "Сохранить"
              )}
            </IconButton>
            <IconButton
              typeIcon="new"
              variant="outlined"
              onClick={(e) => {
                e.preventDefault();
                reset();
                closeForm();
              }}
            >
              Отмена
            </IconButton>
          </div>
        </div>
      </FormBlock>
    </form>
  );
});

export default OrganizationForm;
